import { useCallback, useEffect, useRef, useState } from 'react';
import { isMaximized, maximizeTarget, shrunk } from './pipMaximize';

export function isDocumentPipSupported() {
  return typeof window !== 'undefined' && 'documentPictureInPicture' in window;
}

/**
 * Document Picture-in-Picture(항상 위 작은 창) 열기/닫기.
 *
 * - requestWindow는 사용자 클릭(transient activation)이 있을 때만 가능하므로, 열기(open)는 반드시 클릭 핸들러에서 호출한다.
 *   (화면 공유 선택창을 거친 뒤에는 활성화가 만료될 수 있어 공유 시작 직후 자동으로 열지 않는다)
 * - visible이 false가 되면 앱이 PiP를 닫는다. 사용자가 PiP를 직접 X로 닫은 경우에만 onClosedByUser를 부른다.
 * - PiP 클릭은 게임으로 통과하지 않는다(웹 API 한계). 그래서 게임 중에는 visible을 false로 둬서 닫아 둔다.
 */
export default function usePip({ visible, onClosedByUser, width = 280, height = 110 }) {
  const [pipWindow, setPipWindow] = useState(null);
  const [maximized, setMaximized] = useState(false);
  const restoreRef = useRef(null); // 최대화 전 위치/크기
  const pipWindowRef = useRef(null);
  const closingByAppRef = useRef(false);
  const visibleRef = useRef(visible);
  const onClosedRef = useRef(onClosedByUser);
  visibleRef.current = visible;
  onClosedRef.current = onClosedByUser;

  const open = useCallback(async () => {
    if (pipWindowRef.current || !isDocumentPipSupported()) {
      return;
    }
    try {
      const win = await window.documentPictureInPicture.requestWindow({ width, height });
      pipWindowRef.current = win;
      win.document.body.style.margin = '0';
      // 사용자가 창 크기를 바꾸면 최대화 상태도 다시 판단한다
      win.addEventListener('resize', () => {
        setMaximized(isMaximized({ width: win.outerWidth, height: win.outerHeight }, maximizeTarget(win.screen)));
      });
      win.addEventListener('pagehide', () => {
        const closedByApp = closingByAppRef.current;
        closingByAppRef.current = false;
        pipWindowRef.current = null;
        setPipWindow(null);
        if (!closedByApp && visibleRef.current) {
          onClosedRef.current?.();
        }
      });
      setPipWindow(win);
    } catch (e) {
      // 사용자 클릭 없이 열려 했거나 브라우저가 막은 경우 — 조용히 무시하고 웹의 버튼을 쓰게 한다
      pipWindowRef.current = null;
    }
  }, [width, height]);

  // 최대화: Chrome이 허용하는 최대 크기(모니터 작업 영역의 80%)로 키워 PiP 창이 있는 모니터 가운데에 놓는다. 한 번 더 누르면 이전 위치/크기로 돌아간다.
  // 요청 결과를 확인해 오히려 작아졌으면 원래 크기로 되돌리고 onFail(안내 문구)을 부른다.
  // 브라우저가 막으면 false를 돌려준다(창 이동/크기 조절은 사용자 클릭 직후에만 허용될 수 있다).
  const toggleMaximize = useCallback((onFail) => {
    const win = pipWindowRef.current;
    if (!win) {
      return false;
    }
    try {
      const target = maximizeTarget(win.screen);
      const before = { x: win.screenX, y: win.screenY, width: win.outerWidth, height: win.outerHeight };
      if (isMaximized(before, target) && restoreRef.current) {
        const prev = restoreRef.current;
        win.resizeTo(prev.width, prev.height);
        win.moveTo(prev.x, prev.y);
        restoreRef.current = null;
        setMaximized(false);
        return true;
      }
      restoreRef.current = before;
      win.resizeTo(target.width, target.height);
      win.moveTo(target.left, target.top);
      // 크기 변경은 바로 반영되지 않을 수 있어 잠시 뒤에 결과를 확인한다
      win.setTimeout(() => {
        const after = { width: win.outerWidth, height: win.outerHeight };
        if (shrunk(before, after)) {
          win.resizeTo(before.width, before.height);
          win.moveTo(before.x, before.y);
          restoreRef.current = null;
          setMaximized(false);
          onFail?.('브라우저가 PiP 창을 더 키우지 못하게 해서 원래 크기로 되돌렸어요. (Chrome은 PiP를 모니터의 80%까지만 허용해요)');
        } else {
          setMaximized(isMaximized(after, target));
        }
      }, 300);
      return true;
    } catch (e) {
      return false;
    }
  }, []);

  const closeByApp = useCallback(() => {
    if (pipWindowRef.current) {
      closingByAppRef.current = true;
      pipWindowRef.current.close();
    }
  }, []);

  useEffect(() => {
    if (!visible) {
      closeByApp();
    }
  }, [visible, closeByApp]);

  useEffect(() => closeByApp, [closeByApp]);

  return { pipWindow, open, supported: isDocumentPipSupported(), maximized, toggleMaximize };
}
